import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useI18n } from '../i18n/index.jsx'
import { GlobalCookieConsent } from '../components/GlobalCookieConsent'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useI18n()

  const initialEmail = useMemo(() => (searchParams.get('email') || '').trim(), [searchParams])

  const [email, setEmail] = useState(initialEmail)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    setEmail(initialEmail)
  }, [initialEmail])

  useEffect(() => {
    if (!success) return
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000)
    const to = setTimeout(() => navigate('/login'), 5000)
    return () => { clearInterval(timer); clearTimeout(to) }
  }, [success, navigate])

  async function onSubmit(e) {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!email || !code || !password) {
      setError(t('auth.reset.errors.required'))
      return
    }
    if (password !== confirm) {
      setError(t('auth.reset.errors.mismatch'))
      return
    }

    try {
      setLoading(true)
  const res = await fetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/reset_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          code_vef: code.trim(),
          new_password: password
        })
      })

      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        // Backend error shape: { detail: "Code de vérification incorrect" }
        setError(data?.detail || data?.error || `HTTP ${res.status}`)
        return
      }
      // Success: { message: "Mot de passe réinitialisé avec succès" }
      setSuccess(data?.message || t('auth.reset.success'))
    } catch (err) {
      setError(t('auth.error.server'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-black to-slate-950 text-slate-100">
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
      <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-8 shadow-[0_0_1px_rgba(255,255,255,0.25),0_25px_80px_-20px_rgba(16,185,129,0.35)] backdrop-blur-2xl">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-white/10 ring-1 ring-inset ring-emerald-400/30" />
            <h1 className="text-2xl font-semibold tracking-tight">{t('auth.reset.title')}</h1>
            <p className="mt-1 text-sm text-slate-400">{t('auth.reset.subtitle')}</p>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">
              {success} • Redirection dans {countdown}s…
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm text-slate-300">{t('auth.email')}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none ring-0 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                placeholder="votre@email.com"
                required
                disabled={!!success}
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="code" className="text-sm text-slate-300">{t('auth.reset.code')}</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none ring-0 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40 tracking-widest"
                placeholder="ABC123"
                required
                disabled={!!success}
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="password" className="text-sm text-slate-300">{t('auth.reset.newPassword')}</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none ring-0 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                placeholder="••••••••"
                required
                disabled={!!success}
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="confirm" className="text-sm text-slate-300">{t('auth.reset.confirm')}</label>
              <input
                type="password"
                id="confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none ring-0 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                placeholder="••••••••"
                required
                disabled={!!success}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !!success}
              className="group mt-6 w-full transform rounded-lg bg-emerald-500 py-3 font-semibold text-slate-950 transition hover:-translate-y-0.5 hover:bg-emerald-400 disabled:opacity-50"
            >
              {loading ? t('auth.reset.submitting') : t('auth.reset.submit')}
              <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">→</span>
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            <Link to="/login" className="text-emerald-400 hover:text-emerald-300">{t('auth.backToLogin')}</Link>
          </p>
        </div>
      </div>

      {/* Cookie Consent */}
      <GlobalCookieConsent />
    </div>
  )
}
